import React, { useState, useEffect, useRef } from 'react';
import { HStack, VStack, Box, Text, Icon, Link } from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { useFormContext } from './context/FormContext';
import type { FormStep } from './types';

interface Step {
  id: FormStep;
  label: string;
  hidden?: boolean;
}

const COMPACT_BREAKPOINT = 350; // Switch to compact layout below this width

export const StepNavigator: React.FC = () => {
  const { state, dispatch } = useFormContext();
  const { currentStep, formDetails, mode } = state;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  const hasGifts = (formDetails?.gifts.length ?? 0) > 0;
  const navCurrentStep: FormStep =
    currentStep === 'monthly-upsell' ? 'amount' : currentStep;

  const allSteps: Step[] =
    mode === 'billing-update'
      ? [
          { id: 'amount', label: 'Amount' },
          { id: 'payment', label: 'Payment' },
          { id: 'receipt', label: 'Complete', hidden: true },
        ]
      : [
          { id: 'amount', label: 'Amount' },
          ...(hasGifts ? [{ id: 'gift' as FormStep, label: 'Gift' }] : []),
          { id: 'payment', label: 'Payment' },
          { id: 'information', label: 'Info' },
          { id: 'receipt', label: 'Complete', hidden: true },
        ];

  // Filter out hidden steps for display
  const visibleSteps = allSteps.filter((step) => !step.hidden);
  
  // Find current step index in visible steps only
  const currentStepIndex = visibleSteps.findIndex((step) => step.id === navCurrentStep);

  // Use ResizeObserver to detect when to switch to compact layout
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsCompact(width < COMPACT_BREAKPOINT);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleStepClick = (stepId: FormStep, isClickable: boolean) => {
    if (isClickable) {
      dispatch({ type: 'SET_STEP', payload: stepId });
    }
  };

  return (
    <HStack ref={containerRef} gap={isCompact ? 1.5 : 1.5} py={4} w="full" maxW="full">
      {visibleSteps.map((step, index) => {
        const isCompleted = index < currentStepIndex;
        const isCurrent = index === currentStepIndex;
        const isClickable = isCompleted;

        const circleBg = isCompleted
          ? 'gray.solid'
          : isCurrent
          ? 'gray.muted'
          : 'bg';

        const circleColor = isCompleted ? 'gray.contrast' : 'fg';

        const circleBorderColor = isCompleted
          ? 'gray.solid'
          : isCurrent
          ? 'gray.solid'
          : 'border';

        const Stack = isCompact ? VStack : HStack;
        const stepContent = (
          <Stack gap={isCompact ? 0.5 : 1.5} flexShrink={0}>
            <Box
              w={6}
              h={6}
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg={circleBg}
              color={circleColor}
              borderWidth={2}
              borderColor={circleBorderColor}
              flexShrink={0}
            >
              {isCompleted ? (
                <Icon as={LuCheck} />
              ) : (
                <Text fontSize="xs" fontWeight="medium">
                  {index + 1}
                </Text>
              )}
            </Box>
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="fg"
              textAlign={isCompact ? 'center' : 'left'}
              whiteSpace="nowrap"
            >
              {step.label}
            </Text>
          </Stack>
        );

        return (
          <React.Fragment key={step.id}>
            {isClickable ? (
              <Link
                onClick={() => handleStepClick(step.id, isClickable)}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
              >
                {stepContent}
              </Link>
            ) : (
              stepContent
            )}
            {index < visibleSteps.length - 1 && (
              <Box
                flex={1}
                h={0.5}
                bg="border"
              />
            )}
          </React.Fragment>
        );
      })}
    </HStack>
  );
};

